import { create } from "zustand";
import { persist } from "zustand/middleware";
import * as Sentry from "@sentry/react";
import { PersistedStateSchema } from "./schema";

export interface TimeEntry {
  id: string;
  description: string;
  startTime: number;
  endTime?: number;
  clientId?: string;
}

// An entry is "running" iff it has no end time. The single canonical definition
// of the running-timer state — used by validateSingleRunningEntry (the
// invariant check) and anywhere running entries are counted or filtered, so the
// definition can't drift.
export function isRunningEntry(entry: TimeEntry): boolean {
  return !entry.endTime;
}

export interface Client {
  id: string;
  name: string;
  color: string;
  // Weekly target expressed in productive *days*, not hours. The hours target
  // used for pace comparison is derived as weeklyTargetDays * settings.dayHours
  // at read time, so the target's meaning no longer drifts when the
  // productive-day calibration changes. Migrated from weeklyTargetHours in
  // v4 → v5 (divide by dayHours).
  weeklyTargetDays?: number;
  // Manual priority for the "What did you work on?" stop-timer picker ONLY.
  // Lower sorts first; undefined sorts last (alphabetically among the
  // unordered). Every other client list stays alphabetical / by-total — this
  // field exists so the user can reprioritize the picker without renaming
  // clients. Drag-to-reorder in ClientManager writes it via
  // reorderPickerClients. Added in v5 → v6 (passthrough; absent = unordered).
  pickerOrder?: number;
  // Set when the client is archived; undefined means active. Archived clients
  // stay attached to their historical entries but are hidden from new-entry
  // pickers. Hard delete (via deleteClient) requires archiving first and
  // cascades to every referencing TimeEntry — see CLAUDE.md "no uncategorized
  // entries" principle.
  archivedAt?: number;
}

export type WorkdaysPerWeek = 4 | 5 | 6 | 7;

// Entry-smoothing config. Smoothing is a read-only DERIVED view over the raw
// entries — it never mutates them (see utils/smoothing.ts). When enabled, the
// analytics dashboard and the entries list fold consecutive same-client
// entries separated by a sub-threshold idle gap into one block, so a day of
// fragmented work reads (and bills) as continuous focus. Off by default.
export interface SmoothingSettings {
  enabled: boolean;
  // Gaps at or below this many minutes between two same-client entries are
  // absorbed — provided no other client's work falls inside the gap.
  thresholdMinutes: number;
}

// How many recent qualifying working days the Rhythm panel's Typical column
// pulls from. Fixed preset set — wider = steadier median but slower to
// adapt to habit changes, narrower = more responsive but noisier. Added in
// v7 → v8 (defaults to 20 for both existing and new users). 260 ≈ a working
// year (5 days/week × 52 weeks).
export type RhythmTypicalLookback = 5 | 10 | 20 | 40 | 60 | 180 | 260;

// How many recent Monday-weeks the Work-weeks panel's typical-week average and
// per-week strip span. Fixed preset set, exposed as chips like the Rhythm
// lookback. Added in v8 → v9 (defaults to 12 ≈ a quarter).
export type CadenceLookbackWeeks = 4 | 8 | 12 | 26 | 52;

export interface Settings {
  // How many actually-productive hours count as one billable day. Used to
  // present targets/analytics in days while the underlying numbers stay in
  // hours. Personal calibration — see CLAUDE.md.
  dayHours: number;
  // How many days the user works each week, anchored on Monday. Drives the
  // pacing tick (dow / workdaysPerWeek) and the "work week done" cap on
  // off-days. 4 = Mon–Thu, 5 = Mon–Fri, 6 = Mon–Sat, 7 = Mon–Sun.
  workdaysPerWeek: WorkdaysPerWeek;
  // Entry-smoothing toggle + threshold. Added in v6 → v7 (passthrough; absent
  // = the default below, i.e. smoothing off).
  smoothing: SmoothingSettings;
  // Rhythm panel's Typical-day sample window, in qualifying working days.
  // Read-only outside the panel. Added in v7 → v8.
  rhythmTypicalLookback: RhythmTypicalLookback;
  // Work-weeks panel's lookback window, in Monday-weeks. Read-only outside the
  // panel. Added in v8 → v9.
  cadenceLookbackWeeks: CadenceLookbackWeeks;
}

export const DEFAULT_DAY_HOURS = 8;
export const DEFAULT_WORKDAYS_PER_WEEK: WorkdaysPerWeek = 5;
export const DEFAULT_SMOOTHING: SmoothingSettings = {
  enabled: false,
  thresholdMinutes: 3,
};
export const DEFAULT_RHYTHM_TYPICAL_LOOKBACK: RhythmTypicalLookback = 20;
export const DEFAULT_CADENCE_LOOKBACK_WEEKS: CadenceLookbackWeeks = 12;

interface TimeTrackerState {
  timeEntries: Record<string, TimeEntry>;
  clients: Record<string, Client>;
  settings: Settings;

  // Actions
  addTimeEntry: (entry: TimeEntry) => void;
  updateTimeEntry: (id: string, entry: Partial<TimeEntry>) => void;
  deleteTimeEntry: (id: string) => void;
  getTimeEntries: () => TimeEntry[];
  getTimeEntriesSorted: (
    sortKey: keyof TimeEntry,
    desc?: boolean
  ) => TimeEntry[];
  getTimeEntry: (id: string) => TimeEntry | undefined;

  addClient: (client: Client) => void;
  updateClient: (id: string, client: Partial<Client>) => void;
  reorderPickerClients: (orderedIds: string[]) => void;
  archiveClient: (id: string) => void;
  unarchiveClient: (id: string) => void;
  deleteClient: (id: string) => void;
  getClients: () => Client[];
  getArchivedClients: () => Client[];
  getAllClients: () => Client[];
  getClient: (id: string) => Client | undefined;

  setDayHours: (hours: number) => void;
  setWorkdaysPerWeek: (workdays: WorkdaysPerWeek) => void;
  setSmoothingEnabled: (enabled: boolean) => void;
  setSmoothingThreshold: (minutes: number) => void;
  setRhythmTypicalLookback: (lookback: RhythmTypicalLookback) => void;
  setCadenceLookbackWeeks: (weeks: CadenceLookbackWeeks) => void;

  exportTimeEntries: (format: "csv" | "json") => string;
  pruneAllTimeEntries: () => void;
  pruneOldTimeEntries: (olderThanDays: number) => void;

  importData: (
    data: { clients: Client[]; entries: TimeEntry[] },
    options?: { mode?: "skip" | "overwrite"; dryRun?: boolean },
  ) => ImportResult;
}

export interface ImportResult {
  entries: {
    added: number;
    skipped: number;
    overwritten: number;
    droppedRunning: number;
    unresolvedClient: number;
  };
  clients: {
    added: number;
    reused: number;
    updated: number;
    idConflicts: number;
  };
}

// v0 → v1: "categories" was renamed to "clients" and TimeEntry.categoryId to
// TimeEntry.clientId. Without this migration, existing users would lose their
// categories on first load after the rename ships.
export const migrateV0ToV1 = (persistedState: unknown): unknown => {
  const old = (persistedState ?? {}) as Record<string, unknown>;
  const { categories, ...rest } = old as {
    categories?: Record<string, Client>;
    timeEntries?: Record<string, TimeEntry & { categoryId?: string }>;
  } & Record<string, unknown>;

  const oldEntries = (rest.timeEntries ?? {}) as Record<
    string,
    TimeEntry & { categoryId?: string }
  >;
  const migratedEntries: Record<string, TimeEntry> = {};
  for (const [id, raw] of Object.entries(oldEntries)) {
    const { categoryId, ...entryRest } = raw;
    migratedEntries[id] =
      categoryId !== undefined
        ? { ...entryRest, clientId: categoryId }
        : entryRest;
  }

  return {
    ...rest,
    timeEntries: migratedEntries,
    clients: categories ?? {},
  };
};

// v1 → v2: archivedAt was added to Client and Project. The migration is a
// strict passthrough — every existing client/project remains active because
// archivedAt is optional and absent on prior data. Do NOT set archivedAt
// here; doing so would silently archive every client on first load.
export const migrateV1ToV2 = (persistedState: unknown): unknown =>
  persistedState;

// v3 → v4: settings.workdaysPerWeek was added so the pacing panel can target
// a Mon–Fri / Mon–Sat / Mon–Sun week. The migration fills in the default (5);
// it must not touch any existing weeklyTargetHours — total hours stay the
// same and the daily expectation slides instead.
export const migrateV3ToV4 = (persistedState: unknown): unknown => {
  const old = (persistedState ?? {}) as Record<string, unknown>;
  const settings = (old.settings ?? {}) as Partial<Settings>;
  return {
    ...old,
    settings: {
      dayHours: settings.dayHours ?? DEFAULT_DAY_HOURS,
      workdaysPerWeek:
        settings.workdaysPerWeek ?? DEFAULT_WORKDAYS_PER_WEEK,
    },
  };
};

// v4 → v5: client targets moved from hours to days. Hours was too granular and
// its meaning shifted whenever dayHours (the productive-day definition)
// changed. We now store weeklyTargetDays and derive the hours target as
// days * dayHours at read time. The migration divides each existing
// weeklyTargetHours by the configured dayHours, so the day target shown on
// first load is unchanged — analytics already displayed hoursToDays(target,
// dayHours). Clients without a target stay target-less; the legacy
// weeklyTargetHours field is dropped.
export const migrateV4ToV5 = (persistedState: unknown): unknown => {
  const old = (persistedState ?? {}) as Record<string, unknown>;
  const settings = (old.settings ?? {}) as Partial<Settings>;
  const dayHours = settings.dayHours ?? DEFAULT_DAY_HOURS;
  const oldClients = (old.clients ?? {}) as Record<
    string,
    Client & { weeklyTargetHours?: number }
  >;
  const clients: Record<string, Client> = {};
  for (const [id, raw] of Object.entries(oldClients)) {
    const { weeklyTargetHours, ...rest } = raw;
    clients[id] =
      weeklyTargetHours !== undefined && dayHours > 0
        ? { ...rest, weeklyTargetDays: weeklyTargetHours / dayHours }
        : rest;
  }
  return { ...old, clients };
};

// v5 → v6: Client.pickerOrder was added so the stop-timer picker can be
// reordered without renaming clients. Strict passthrough — every existing
// client stays unordered (pickerOrder absent), so the picker keeps its
// alphabetical order until the user drags. Do NOT assign an order here; the
// only signal for "the user has a preferred order" is their first drag.
export const migrateV5ToV6 = (persistedState: unknown): unknown =>
  persistedState;

// v6 → v7: settings.smoothing was added so entry smoothing can be toggled and
// tuned. The migration fills in the default (off, 3-minute threshold) when the
// field is absent — every existing user lands with smoothing disabled, so the
// app behaves exactly as before until they turn it on. This MUST run before
// the zod parse in merge(): the v7 SettingsSchema requires `smoothing`, so a
// pre-v7 payload that skipped this would fail validation and fall back to a
// fresh store. Existing dayHours/workdaysPerWeek are left untouched.
export const migrateV6ToV7 = (persistedState: unknown): unknown => {
  const old = (persistedState ?? {}) as Record<string, unknown>;
  const settings = (old.settings ?? {}) as Partial<Settings>;
  return {
    ...old,
    settings: {
      dayHours: settings.dayHours ?? DEFAULT_DAY_HOURS,
      workdaysPerWeek: settings.workdaysPerWeek ?? DEFAULT_WORKDAYS_PER_WEEK,
      smoothing: settings.smoothing ?? DEFAULT_SMOOTHING,
    },
  };
};

// v7 → v8: settings.rhythmTypicalLookback was added so the Rhythm panel's
// Typical column window is user-tunable across the 5/10/20 presets. Like
// migrateV6ToV7, this MUST run before the zod parse in merge() — the v8
// SettingsSchema requires the new field. Existing users land on the default
// 20, which matches the shipped behavior before v7→v8 introduced any choice.
export const migrateV7ToV8 = (persistedState: unknown): unknown => {
  const old = (persistedState ?? {}) as Record<string, unknown>;
  const settings = (old.settings ?? {}) as Partial<Settings>;
  return {
    ...old,
    settings: {
      dayHours: settings.dayHours ?? DEFAULT_DAY_HOURS,
      workdaysPerWeek: settings.workdaysPerWeek ?? DEFAULT_WORKDAYS_PER_WEEK,
      smoothing: settings.smoothing ?? DEFAULT_SMOOTHING,
      rhythmTypicalLookback:
        settings.rhythmTypicalLookback ?? DEFAULT_RHYTHM_TYPICAL_LOOKBACK,
    },
  };
};

// v8 → v9: settings.cadenceLookbackWeeks was added so the Work-weeks panel's
// lookback window is user-tunable across the 4/8/12/26/52 presets. Like the
// migrations above, this MUST run before the zod parse in merge() — the v9
// SettingsSchema requires the new field. Existing users land on the default 12.
export const migrateV8ToV9 = (persistedState: unknown): unknown => {
  const old = (persistedState ?? {}) as Record<string, unknown>;
  const settings = (old.settings ?? {}) as Partial<Settings>;
  return {
    ...old,
    settings: {
      dayHours: settings.dayHours ?? DEFAULT_DAY_HOURS,
      workdaysPerWeek: settings.workdaysPerWeek ?? DEFAULT_WORKDAYS_PER_WEEK,
      smoothing: settings.smoothing ?? DEFAULT_SMOOTHING,
      rhythmTypicalLookback:
        settings.rhythmTypicalLookback ?? DEFAULT_RHYTHM_TYPICAL_LOOKBACK,
      cadenceLookbackWeeks:
        settings.cadenceLookbackWeeks ?? DEFAULT_CADENCE_LOOKBACK_WEEKS,
    },
  };
};

// v2 → v3: the Project model was removed (no UI ever surfaced it; every entry
// was hardcoded to projectId "p1"). Drop the `projects` slice and strip
// `projectId` from every TimeEntry so the v3 schema validates.
export const migrateV2ToV3 = (persistedState: unknown): unknown => {
  const old = (persistedState ?? {}) as Record<string, unknown>;
  const { projects: _projects, ...rest } = old as {
    projects?: unknown;
    timeEntries?: Record<string, TimeEntry & { projectId?: string }>;
  } & Record<string, unknown>;
  void _projects;

  const oldEntries = (rest.timeEntries ?? {}) as Record<
    string,
    TimeEntry & { projectId?: string }
  >;
  const migratedEntries: Record<string, TimeEntry> = {};
  for (const [id, raw] of Object.entries(oldEntries)) {
    const { projectId: _projectId, ...entryRest } = raw;
    void _projectId;
    migratedEntries[id] = entryRest;
  }

  return { ...rest, timeEntries: migratedEntries };
};

export const useTimeTrackerStore = create<TimeTrackerState>()(
  persist(
    (set, get) => ({
      timeEntries: {},
      clients: {},
      settings: {
        dayHours: DEFAULT_DAY_HOURS,
        workdaysPerWeek: DEFAULT_WORKDAYS_PER_WEEK,
        smoothing: DEFAULT_SMOOTHING,
        rhythmTypicalLookback: DEFAULT_RHYTHM_TYPICAL_LOOKBACK,
        cadenceLookbackWeeks: DEFAULT_CADENCE_LOOKBACK_WEEKS,
      },

      addTimeEntry: (entry) =>
        set((state) => ({
          timeEntries: { ...state.timeEntries, [entry.id]: entry },
        })),

      updateTimeEntry: (id, entry) =>
        set((state) => ({
          timeEntries: {
            ...state.timeEntries,
            [id]: { ...state.timeEntries[id], ...entry },
          },
        })),

      deleteTimeEntry: (id) =>
        set((state) => {
          const newTimeEntries = { ...state.timeEntries };
          delete newTimeEntries[id];
          return { timeEntries: newTimeEntries };
        }),

      getTimeEntries: () => {
        const state = get();
        return Object.values(state.timeEntries);
      },

      getTimeEntriesSorted: (sortKey, desc = false) => {
        const entries = Object.values(get().timeEntries);
        return entries.sort((a, b) => {
          if (desc) {
            return (b[sortKey] as number) > (a[sortKey] as number) ? 1 : -1;
          }
          return (a[sortKey] as number) > (b[sortKey] as number) ? 1 : -1;
        });
      },

      getTimeEntry: (id) => get().timeEntries[id],

      addClient: (client) =>
        set((state) => ({
          clients: { ...state.clients, [client.id]: client },
        })),

      updateClient: (id, client) =>
        set((state) => ({
          clients: {
            ...state.clients,
            [id]: { ...state.clients[id], ...client },
          },
        })),

      // Rewrites pickerOrder densely (0, 1, 2, …) across the clients named in
      // orderedIds, in the order given. Drives the stop-timer picker's sort and
      // nothing else. Ids that don't exist are skipped; clients not named keep
      // their current pickerOrder untouched (e.g. archived clients absent from
      // the reorder list), so a later restore just tie-breaks by name until the
      // user drags again.
      reorderPickerClients: (orderedIds) =>
        set((state) => {
          const clients = { ...state.clients };
          orderedIds.forEach((id, index) => {
            const client = clients[id];
            if (client) clients[id] = { ...client, pickerOrder: index };
          });
          return { clients };
        }),

      archiveClient: (id) => {
        const state = get();
        const client = state.clients[id];
        if (!client) return;
        if (client.archivedAt !== undefined) return;

        // Block archiving the client of the running timer so a stopped entry
        // can't end up committed against an archived client. The user is
        // surfaced this via the ClientManager handler as a warning toast.
        const running = Object.values(state.timeEntries).find(
          (e) => e.endTime === undefined && e.clientId === id,
        );
        if (running) {
          throw new Error(
            `archiveClient: cannot archive ${id} while a timer references it; stop the timer first`,
          );
        }

        // Keep the user from archiving their last active client — the timer
        // form would be unusable. They can add another, then archive this one.
        const activeCount = Object.values(state.clients).filter(
          (c) => c.archivedAt === undefined,
        ).length;
        if (activeCount <= 1) {
          throw new Error(
            "archiveClient: cannot archive the last active client; add another first",
          );
        }

        set({
          clients: {
            ...state.clients,
            [id]: { ...client, archivedAt: Date.now() },
          },
        });
      },

      unarchiveClient: (id) =>
        set((state) => {
          const client = state.clients[id];
          if (!client) return state;
          if (client.archivedAt === undefined) return state;
          const { archivedAt: _archivedAt, ...rest } = client;
          void _archivedAt;
          return { clients: { ...state.clients, [id]: rest } };
        }),

      // Hard delete. Refuses to run on a non-archived client so the
      // archive-first workflow stays the only path here. Cascades to every
      // referencing TimeEntry — see CLAUDE.md "no uncategorized entries".
      deleteClient: (id) => {
        const client = get().clients[id];
        if (!client) return;
        if (client.archivedAt === undefined) {
          throw new Error(
            `deleteClient: client ${id} must be archived before it can be deleted`,
          );
        }
        set((state) => {
          const newClients = { ...state.clients };
          delete newClients[id];
          const newTimeEntries: Record<string, TimeEntry> = {};
          for (const [eid, entry] of Object.entries(state.timeEntries)) {
            if (entry.clientId !== id) newTimeEntries[eid] = entry;
          }
          return { clients: newClients, timeEntries: newTimeEntries };
        });
      },

      getClients: () =>
        Object.values(get().clients).filter((c) => c.archivedAt === undefined),

      getArchivedClients: () =>
        Object.values(get().clients).filter((c) => c.archivedAt !== undefined),

      getAllClients: () => Object.values(get().clients),

      getClient: (id) =>
        id === "running"
          ? { id: "running", name: "Tracker is running", color: "#000000" }
          : get().clients[id],

      setDayHours: (hours) =>
        set((state) => ({
          settings: { ...state.settings, dayHours: hours },
        })),

      setWorkdaysPerWeek: (workdays) =>
        set((state) => ({
          settings: { ...state.settings, workdaysPerWeek: workdays },
        })),

      setSmoothingEnabled: (enabled) =>
        set((state) => ({
          settings: {
            ...state.settings,
            smoothing: { ...state.settings.smoothing, enabled },
          },
        })),

      setSmoothingThreshold: (minutes) =>
        set((state) => ({
          settings: {
            ...state.settings,
            smoothing: { ...state.settings.smoothing, thresholdMinutes: minutes },
          },
        })),

      setRhythmTypicalLookback: (lookback) =>
        set((state) => ({
          settings: { ...state.settings, rhythmTypicalLookback: lookback },
        })),

      setCadenceLookbackWeeks: (weeks) =>
        set((state) => ({
          settings: { ...state.settings, cadenceLookbackWeeks: weeks },
        })),

      exportTimeEntries: (format) => {
        const { timeEntries } = get();
        const entries = Object.values(timeEntries);

        const sortedEntries = entries.sort((a, b) => b.startTime - a.startTime);

        if (format === "json") {
          // Full-fidelity backup: clients (including archived) ride along
          // with the entries so a JSON import can rebuild the workspace
          // exactly. CSV stays entry-only and one-way (see DataManagement
          // copy) because its spreadsheet shape can't carry client metadata
          // cleanly.
          const sortedClients = Object.values(get().clients).sort((a, b) =>
            a.name.localeCompare(b.name),
          );
          const exportData = {
            version: 1,
            exportedAt: Date.now(),
            clients: sortedClients,
            entries: sortedEntries,
          };

          return JSON.stringify(exportData, null, 2);
        } else {
          const header = "Date,Start Time,End Time,Client,Description\n";

          const rows = sortedEntries
            .map((entry) => {
              const startDate = new Date(entry.startTime);
              const endDate = entry.endTime ? new Date(entry.endTime) : null;

              const date = startDate.toISOString().split("T")[0];
              const startTimeStr = startDate.toISOString();
              const endTimeStr = endDate ? endDate.toISOString() : "";

              const client = entry.clientId
                ? get().clients[entry.clientId]
                : null;
              const clientName = client ? client.name : "";

              const safeDescription = entry.description.replace(/"/g, '""');

              return `${date},${startTimeStr},${endTimeStr},${clientName},"${safeDescription}"`;
            })
            .join("\n");

          return header + rows;
        }
      },

      pruneAllTimeEntries: () => set({ timeEntries: {} }),

      pruneOldTimeEntries: (olderThanDays) =>
        set((state) => {
          const cutoffTime = Date.now() - olderThanDays * 24 * 60 * 60 * 1000;
          const newTimeEntries = { ...state.timeEntries };

          Object.entries(newTimeEntries).forEach(([id, entry]) => {
            if (entry.startTime < cutoffTime) {
              delete newTimeEntries[id];
            }
          });

          return { timeEntries: newTimeEntries };
        }),

      // Merges an imported { clients, entries } payload into the store.
      //
      // Client merge rules (incoming → local):
      //   * id match + name match (case-insensitive trim):
      //       skip-mode → reuse local as-is
      //       overwrite-mode → rewrite color/target/archivedAt
      //   * id match + name mismatch (id collision with different client):
      //       mint a fresh local id, add incoming as a new client, remap
      //       any incoming entries referencing the original id
      //   * id miss + name match:
      //       reuse local id, remap incoming entries' clientId to local id
      //   * id miss + name miss:
      //       add incoming as a new client under its original id
      //
      // Entry merge rules:
      //   * endTime === undefined → drop (preserves the single-running-timer
      //     invariant; the local timer is the source of truth for running
      //     state, never an import)
      //   * id match in local → skip or overwrite based on mode
      //   * clientId can't be resolved against any local or imported client
      //     after remap → strip clientId; the entry survives without a
      //     client assignment
      //   * otherwise insert
      //
      // Single atomic set: the new state is built fully in-memory before
      // commit so a thrown error mid-way leaves the store untouched.
      importData: (data, options) => {
        const mode = options?.mode ?? "skip";
        const dryRun = options?.dryRun ?? false;
        const state = get();

        const newClients: Record<string, Client> = { ...state.clients };
        const clientIdRemap: Record<string, string> = {};

        const localByName = new Map<string, string>();
        for (const c of Object.values(newClients)) {
          localByName.set(c.name.trim().toLowerCase(), c.id);
        }

        const clientCounts = {
          added: 0,
          reused: 0,
          updated: 0,
          idConflicts: 0,
        };

        for (const inc of data.clients) {
          const incName = inc.name.trim().toLowerCase();
          const local = newClients[inc.id];

          if (local) {
            const localName = local.name.trim().toLowerCase();
            if (localName === incName) {
              if (mode === "overwrite") {
                newClients[inc.id] = { ...local, ...inc };
                clientCounts.updated += 1;
              } else {
                clientCounts.reused += 1;
              }
              continue;
            }

            // Id collision with a different client. Mint a fresh id and
            // remap any referencing incoming entries.
            const mintedId = `cl_imp_${Date.now()}_${clientCounts.added}`;
            newClients[mintedId] = { ...inc, id: mintedId };
            clientIdRemap[inc.id] = mintedId;
            localByName.set(incName, mintedId);
            clientCounts.added += 1;
            clientCounts.idConflicts += 1;
            continue;
          }

          const byName = localByName.get(incName);
          if (byName) {
            clientIdRemap[inc.id] = byName;
            clientCounts.reused += 1;
            continue;
          }

          newClients[inc.id] = inc;
          localByName.set(incName, inc.id);
          clientCounts.added += 1;
        }

        const newTimeEntries: Record<string, TimeEntry> = {
          ...state.timeEntries,
        };
        const entryCounts = {
          added: 0,
          skipped: 0,
          overwritten: 0,
          droppedRunning: 0,
          unresolvedClient: 0,
        };

        for (const inc of data.entries) {
          if (inc.endTime === undefined) {
            entryCounts.droppedRunning += 1;
            continue;
          }

          let resolved: TimeEntry = { ...inc };
          if (resolved.clientId !== undefined) {
            const remapped = clientIdRemap[resolved.clientId];
            const effectiveId = remapped ?? resolved.clientId;
            if (newClients[effectiveId]) {
              resolved = { ...resolved, clientId: effectiveId };
            } else {
              const { clientId: _drop, ...rest } = resolved;
              void _drop;
              resolved = rest;
              entryCounts.unresolvedClient += 1;
            }
          }

          const existing = newTimeEntries[resolved.id];
          if (existing) {
            if (mode === "overwrite") {
              newTimeEntries[resolved.id] = resolved;
              entryCounts.overwritten += 1;
            } else {
              entryCounts.skipped += 1;
            }
            continue;
          }

          newTimeEntries[resolved.id] = resolved;
          entryCounts.added += 1;
        }

        if (!dryRun) {
          set({ clients: newClients, timeEntries: newTimeEntries });
        }

        return { entries: entryCounts, clients: clientCounts };
      },
    }),
    {
      name: "time-tracker-store",
      version: 9,
      migrate: (persistedState, version) => {
        let state = persistedState;
        if (version < 1) state = migrateV0ToV1(state);
        if (version < 2) state = migrateV1ToV2(state);
        if (version < 3) state = migrateV2ToV3(state);
        if (version < 4) state = migrateV3ToV4(state);
        if (version < 5) state = migrateV4ToV5(state);
        if (version < 6) state = migrateV5ToV6(state);
        if (version < 7) state = migrateV6ToV7(state);
        if (version < 8) state = migrateV7ToV8(state);
        if (version < 9) state = migrateV8ToV9(state);
        return state as TimeTrackerState;
      },
      // After migrate, validate the persisted shape with zod. A failure here
      // is loud (Sentry) and recoverable (fall back to the fresh in-memory
      // store) so corrupted localStorage can't crash the app.
      merge: (persistedState, currentState) => {
        const parsed = PersistedStateSchema.safeParse(persistedState);
        if (!parsed.success) {
          Sentry.captureException(
            new Error("Persisted time-tracker state failed schema validation"),
            { contexts: { zod: { issues: parsed.error.issues } } },
          );
          return currentState;
        }
        return { ...currentState, ...parsed.data };
      },
    }
  )
);

// Initialize with default clients if needed
export const initTimeTrackerState = () => {
  const { clients, addClient } = useTimeTrackerStore.getState();

  if (Object.keys(clients).length === 0) {
    addClient({
      id: "c1",
      name: "Work",
      color: "#10b981",
      weeklyTargetDays: 5,
    });

    addClient({
      id: "c2",
      name: "Personal",
      color: "#8b5cf6",
      weeklyTargetDays: 1,
    });
  }
};
